// Indexes a collection and subcollections (if any)

import PropTypes from 'prop-types'
import styled from 'styled-components'
import { NavLink, Route, useRouteMatch } from 'react-router-dom'

import { Box, Text } from '@redriverpress/system'

import getInitialState from 'Common/utils/getInitialState'

const CollectionLink = styled(NavLink).attrs({
  activeClassName: 'active',
  exact: true,
})`
  color: ${props => props.theme.colors[props.color]};
  background: none;
  border-radius: ${props => props.theme.radii.rounded};
  display: block;
  font-weight: 600;
  padding: 0.5rem;
  margin: 0.25rem 0;
  text-decoration: none;

  &.active {
    background: ${props => props.theme.colors.blue600};
    color: white;
  }
`

// Recursively renders subcollection links
function NestedCollection({ title, slug, subCollections, empty }) {
  const { url } = useRouteMatch()

  // Prevent double forward slash (//) in urls
  // when the current url is the root path.
  // This won't be an issue in React Router v6.
  const resolvedUrl = url === '/' ? '/' : `${url}/`

  return (
    <Box>
      <CollectionLink
        color={empty ? 'gray500' : 'black'}
        to={`${resolvedUrl}${slug}`}
      >
        {title}
      </CollectionLink>
      {subCollections.length > 0 &&
        subCollections.map(sc => (
          <Route key={title} path={`${resolvedUrl}${slug}`}>
            {/* Further NestedCollections wrapped in a Route with their
                parent's slug so the links are shown progressively. */}
            <Box pl={3}>
              <NestedCollection
                title={sc.title}
                slug={sc.slug}
                subCollections={sc.subCollections}
                empty={sc.empty}
              />
            </Box>
          </Route>
        ))}
    </Box>
  )
}

NestedCollection.defaultProps = {
  empty: false,
  subCollections: [],
}

NestedCollection.propTypes = {
  title: PropTypes.string.isRequired,
  slug: PropTypes.string.isRequired,
  empty: PropTypes.bool,
  subCollections: PropTypes.arrayOf(PropTypes.object),
}

// Renders a link for the top level collection,
// and for its subcollectionos (if any) using NestedCollection
export default function CollectionIndex() {
  const { subCollections } = getInitialState()
  return (
    <Box
      as='nav'
      px={[0, null, null, 3]}
      py={3}
      borderRadius='rounded'
      bg='white'
      id='collectionIndex'
    >
      {/* Above id required for aria-controls attribute in ListIndexMenu */}
      <Text fontSize={[1, null, 2]}>
        <CollectionLink to='/'>All Materials</CollectionLink>
      </Text>
      {subCollections.length > 0 && (
        <Box fontSize={[0, null, null, 1]}>
          {subCollections.map(sc => (
            <NestedCollection
              key={sc.title}
              title={sc.title}
              slug={sc.slug}
              subCollections={sc.subCollections}
              empty={sc.empty}
            />
          ))}
        </Box>
      )}
    </Box>
  )
}
